/**
 * Type definition for the data object.
 */
type Data = Record<string, any>;

/**
 * Type definition for the property object.
 * It extends the Data type and adds a 'usages' property which is an array of Data objects.
 */
type Property = Data & { usages: Data[] };

/**
 * Type definition for the PermIdentifiedProperty object.
 * It extends the Data type and adds a 'usages' property which is an array of objects that have a 'hasAccess' property of type boolean.
 */
type PermIdentifiedProperty = Data & { usages: Array<{ hasAccess: boolean } & Data> };

/**
 * Function that identifies access for usages.
 * @param store - The storage object.
 * @returns A function that takes a Property object as input and returns a PermIdentifiedProperty object.
 */
export function IdentifyAccessForUsages(store: Storage) {
  return (record: Property): PermIdentifiedProperty => {
    const { userLocationPerms = null, userRecordPerm = null } = store;
    if (record != null && Array.isArray(record?.usages) && record.usages.length > 0) {
      const accessToAllUsages = recordExistsInRecordPerms(record, JSON.parse(userRecordPerm) ?? []);
      record.usages.forEach(usage => {
        usage.hasAccess = accessToAllUsages || hasAccessToUsage(usage, JSON.parse(userLocationPerms) ?? []);
      });
    }
    return record as PermIdentifiedProperty;
  };
}

/**
 * Function that checks if a record exists in the record permissions.
 * @param record - The record object.
 * @param recordPerms - The record permissions array.
 * @returns A boolean indicating whether the record exists in the record permissions.
 */
const recordExistsInRecordPerms = (record: Data, recordPerms: Data[]): boolean => {
  return recordPerms?.some(perm => perm.recordId === record?.entity?.id);
};

/**
 * Function that filters permissions based on a property and value.
 * @param permissions - The permissions array.
 * @param property - The property to filter on.
 * @param value - The value to filter on.
 * @returns The filtered permissions array.
 */
const filterPermissions = (permissions: Data[], property: string, value: any) =>
  permissions.filter(perm => [value, 'All'].includes(perm[property]));

/**
 * Function that checks if there is access to a usage.
 * @param params - The usage parameters.
 * @param locationPerms - The location permissions array.
 * @returns A boolean indicating whether there is access to the usage.
 */
const hasAccessToUsage = (params: Data, locationPerms: Data[]): boolean => {
  const { ref_property_usage_type_desc, lig_geography = {} } = params;
  const filters = [
    // Filter those location permissions that match the usage's property usage type
    { property: 'propertyUsageTypeVal', value: ref_property_usage_type_desc },
    // Filter those location permissions that match the usage's globalRegionVal
    { property: 'globalRegionVal', value: lig_geography?.global_region_desc },
    // Filter those location permissions that match the usage's divisionVal
    { property: 'divisionVal', value: lig_geography?.division_desc },
    // Filter those location permissions that match the usage's marketVal
    { property: 'marketVal', value: lig_geography?.market_desc },
    // Filter those location permissions that match the usage's subMarketVal
    { property: 'subMarketVal', value: lig_geography?.submarket_desc },
  ];

  return (
    filters.reduce((perms, filter) => filterPermissions(perms, filter.property, filter.value), locationPerms).length > 0
  );
};
